import { useState } from "react";
import {
  EyeOff,
  RotateCcw,
  ShieldCheck,
  UserPlus,
  Users,
  Sparkles,
  Search,
  Coins,
  RefreshCw,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { thaiTime } from "./types";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface TeamDirectoryTabProps {
  managedEmployees: any;
  employeeDraft: {
    id?: number;
    displayName: string;
    employeeCode: string;
    department: string;
  };
  setEmployeeDraft: React.Dispatch<
    React.SetStateAction<{
      id?: number;
      displayName: string;
      employeeCode: string;
      department: string;
    }>
  >;
  saveEmployee: () => void;
  saveManagedEmployee: any;
  setManagedEmployeeActive: any;
  deleteManagedEmployee: any;
  hiddenEmployees: any;
  visibleSourceNames: string[];
  restoreEmployee: any;
  hideEmployee: any;
  idToken: string | null;
}

export function TeamDirectoryTab({
  managedEmployees,
  employeeDraft,
  setEmployeeDraft,
  saveEmployee,
  saveManagedEmployee,
  setManagedEmployeeActive,
  deleteManagedEmployee,
  hiddenEmployees,
  visibleSourceNames,
  restoreEmployee,
  hideEmployee,
  idToken,
}: TeamDirectoryTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);

  const profilesQuery = trpc.payroll.profiles.useQuery(
    { idToken: idToken || "" },
    { enabled: Boolean(idToken) }
  );

  const seed30Mutation = trpc.salaryAdvance.seed30.useMutation({
    onSuccess: (data) => {
      toast.success(`นำเข้ารายชื่อพนักงานและอัตราค่าแรง ${data.count} คน สำเร็จ`);
      managedEmployees.refetch();
      profilesQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "เกิดข้อผิดพลาดในการนำเข้าพนักงาน");
    },
  });

  const handleSeed30 = () => {
    if (!idToken) {
      toast.error("กรุณาเข้าสู่ระบบก่อนทำรายการ");
      return;
    }
    if (window.confirm("ต้องการซิงค์รายชื่อพนักงานจาก Google Sheet (14 คน) พร้อมอัตราค่าแรงรายวัน หรือไม่?")) {
      seed30Mutation.mutate({ idToken });
    }
  };

  const profiles = profilesQuery.data ?? [];
  const profileMap = new Map(profiles.map((p: any) => [p.displayName, p]));

  // Combine managed employees with salary profiles
  const employeeList = (managedEmployees.data ?? []).map((emp: any) => {
    const prof = profileMap.get(emp.displayName);
    return {
      ...emp,
      dailyRate: prof?.dailyRate ?? (prof?.monthlySalary ? Math.round(prof.monthlySalary / 26) : 500),
      monthlySalary: prof?.monthlySalary ?? null,
      otherAllowances: prof?.otherAllowances ?? 0,
    };
  });

  const filtered = employeeList.filter((emp: any) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      emp.displayName.toLowerCase().includes(q) ||
      (emp.employeeCode && emp.employeeCode.toLowerCase().includes(q)) ||
      (emp.department && emp.department.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-4">
      {/* Header & Sync Sheet Employees Action */}
      <Card className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-lg">
        <div className="h-1.5 bg-gradient-to-r from-[#1b6b44] via-[#248a58] to-[#6ee7b7]" />

        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-[#1b6b44]">
                  รายชื่อพนักงานจาก Google Sheet (14 คน)
                </span>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-black text-slate-900">
                ข้อมูลพนักงาน ค่าแรง และสิทธิ์เบิกเงิน
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-slate-500 font-medium">
                ข้อมูลตรงจาก Google Sheet: รหัสพนักงาน · รายชื่อ-สกุล · ค่าแรงรายวัน · ขอเบิกเงินล่วงหน้า
              </CardDescription>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSeed30}
                disabled={seed30Mutation.isPending}
                className="btn-primary-teal rounded-full text-xs font-black shadow-xs"
              >
                <Sparkles size={14} className={`mr-1.5 ${seed30Mutation.isPending ? "animate-spin" : ""}`} />
                ซิงค์จาก Google Sheet (14 คน)
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Rule Card: Daily 500 * days = max advance limit */}
          <div className="rounded-2xl bg-[#ebf6ee] border border-[#cbe4d2] p-3.5 text-xs text-[#134e31] space-y-1 shadow-2xs mb-4">
            <p className="font-black text-sm text-[#1b6b44] flex items-center gap-1.5">
              <Coins size={16} /> กฎการเบิกเงินล่วงหน้า (Early Wage Access)
            </p>
            <p className="leading-relaxed">
              เช่น พนักงานค่าแรงวันละ <strong>500 บาท</strong> ทำงานไปแล้ว 3 วัน = <strong>1,500 บาท</strong> — 
              ระบบจะไม่อนุญาตให้เบิกเงินล่วงหน้าเกินยอดค่าแรงที่ทำงานไปแล้วในรอบบิล (ตัดรอบทุกวันที่ 10 และ 25)
            </p>
          </div>

          {/* Add / Edit Inline Form */}
          <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-4 space-y-3">
            <h4 className="font-black text-xs sm:text-sm text-slate-800">
              {employeeDraft.id ? "แก้ไขข้อมูลพนักงาน" : "เพิ่มพนักงานใหม่"}
            </h4>

            <div className="grid gap-2 sm:grid-cols-3">
              <Input
                placeholder="ชื่อ-นามสกุลพนักงาน"
                value={employeeDraft.displayName}
                onChange={(e) =>
                  setEmployeeDraft({ ...employeeDraft, displayName: e.target.value })
                }
                className="h-10 rounded-xl text-xs bg-white"
              />
              <Input
                placeholder="รหัสพนักงาน (เช่น EMP031)"
                value={employeeDraft.employeeCode}
                onChange={(e) =>
                  setEmployeeDraft({ ...employeeDraft, employeeCode: e.target.value })
                }
                className="h-10 rounded-xl text-xs bg-white"
              />
              <Input
                placeholder="แผนก (เช่น บริการ, คลังสินค้า)"
                value={employeeDraft.department}
                onChange={(e) =>
                  setEmployeeDraft({ ...employeeDraft, department: e.target.value })
                }
                className="h-10 rounded-xl text-xs bg-white"
              />
            </div>

            <div className="flex gap-2">
              <Button
                className="btn-primary-teal h-9 flex-1 rounded-xl font-bold text-xs"
                onClick={saveEmployee}
                disabled={!employeeDraft.displayName || saveManagedEmployee.isPending}
              >
                {employeeDraft.id ? "บันทึกการแก้ไข" : "เพิ่มพนักงาน"}
              </Button>
              {employeeDraft.id && (
                <Button
                  variant="outline"
                  className="h-9 rounded-xl font-bold text-xs"
                  onClick={() =>
                    setEmployeeDraft({
                      displayName: "",
                      employeeCode: "",
                      department: "",
                    })
                  }
                >
                  ยกเลิก
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Directory Table / Cards */}
      <Card className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-[#1b6b44]" />
              <CardTitle className="text-base font-black text-slate-900">
                รายชื่อพนักงานทั้งหมด ({filtered.length} คน)
              </CardTitle>
            </div>

            <div className="relative w-full sm:w-64">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="ค้นหารหัส หรือชื่อพนักงาน..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 text-xs h-9 rounded-xl border-slate-200 bg-white"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-3 sm:px-6 pb-6">
          <div className="space-y-2 max-h-[550px] overflow-y-auto pr-1">
            {filtered.map((item: any) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedEmployee({ ...item })}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") setSelectedEmployee({ ...item });
                }}
                className="cursor-pointer rounded-2xl border border-slate-200/80 bg-white/90 p-3 hover:border-[#1b6b44]/60 hover:bg-emerald-50/30 transition shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-numeric font-black text-xs px-2 py-0.5 rounded-lg bg-emerald-100 text-emerald-900">
                        {item.employeeCode || "EMP---"}
                      </span>
                      <p className="font-black text-sm text-slate-900 truncate">
                        {item.displayName}
                      </p>
                      {item.department && (
                        <Badge variant="outline" className="text-[10px] py-0 border-slate-200 text-slate-600">
                          {item.department}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 font-semibold mt-1">
                      <span>
                        ค่าแรง:{" "}
                        <strong className="font-numeric text-[#1b6b44]">
                          ฿{item.dailyRate?.toLocaleString()}/วัน
                        </strong>
                      </span>
                      {item.otherAllowances > 0 && (
                        <span>
                          ค่าอื่นๆ:{" "}
                          <strong className="font-numeric text-teal-700">
                            ฿{item.otherAllowances.toLocaleString()}
                          </strong>
                        </span>
                      )}
                      <span>
                        สิทธิ์เบิกเงิน:{" "}
                        <strong className="text-emerald-700">คำนวณตามวันทำงานจริง</strong>
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2.5 text-xs rounded-xl font-bold border-slate-200"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEmployeeDraft({
                          id: item.id,
                          displayName: item.displayName,
                          employeeCode: item.employeeCode ?? "",
                          department: item.department ?? "",
                        });
                      }}
                    >
                      แก้ไข
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2 text-xs rounded-xl border-rose-200 text-rose-700 hover:bg-rose-50"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (window.confirm(`ยืนยันการลบ ${item.displayName} หรือไม่`)) deleteManagedEmployee.mutate({
                          idToken: idToken!,
                          id: item.id,
                        });
                      }}
                      disabled={deleteManagedEmployee.isPending}
                    >
                      ลบ
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedEmployee)} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader className="pr-8">
            <DialogTitle className="text-xl font-black">ข้อมูลพนักงานรายบุคคล</DialogTitle>
            <DialogDescription>แก้ไขข้อมูลและสถานะการใช้งานของพนักงานรายนี้</DialogDescription>
          </DialogHeader>
          {selectedEmployee && (
            <div className="space-y-3">
              <Input
                aria-label="ชื่อ-นามสกุลพนักงาน"
                value={selectedEmployee.displayName ?? ""}
                onChange={(event) => setSelectedEmployee({ ...selectedEmployee, displayName: event.target.value })}
                placeholder="ชื่อ-นามสกุล"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  aria-label="รหัสพนักงาน"
                  value={selectedEmployee.employeeCode ?? ""}
                  onChange={(event) => setSelectedEmployee({ ...selectedEmployee, employeeCode: event.target.value })}
                  placeholder="รหัสพนักงาน"
                />
                <Input
                  aria-label="แผนก"
                  value={selectedEmployee.department ?? ""}
                  onChange={(event) => setSelectedEmployee({ ...selectedEmployee, department: event.target.value })}
                  placeholder="แผนก"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm">
                <div><p className="font-bold text-slate-500">ค่าแรงรายวัน</p><p className="font-black text-emerald-800">฿{Number(selectedEmployee.dailyRate ?? 0).toLocaleString()}</p></div>
                <div><p className="font-bold text-slate-500">สถานะ</p><p className="font-black text-slate-900">{selectedEmployee.isActive === 0 ? "ปิดใช้งาน" : "ใช้งานอยู่"}</p></div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {selectedEmployee && (
              <Button
                variant="outline"
                onClick={() => {
                  setManagedEmployeeActive.mutate({ idToken: idToken!, id: selectedEmployee.id, isActive: selectedEmployee.isActive === 0 });
                  setSelectedEmployee(null);
                }}
              >
                {selectedEmployee.isActive === 0 ? "เปิดใช้งาน" : "ปิดใช้งาน"}
              </Button>
            )}
            <Button
              className="btn-primary-teal"
              disabled={!selectedEmployee?.displayName || saveManagedEmployee.isPending}
              onClick={() => {
                saveManagedEmployee.mutate({
                  idToken: idToken!,
                  id: selectedEmployee.id,
                  displayName: selectedEmployee.displayName,
                  employeeCode: selectedEmployee.employeeCode || null,
                  department: selectedEmployee.department || null,
                });
                setSelectedEmployee(null);
              }}
            >
              บันทึกข้อมูล
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
